/** Analytics day labels: each direction judged against the break-even of its own picks' odds. */

import { normalizeAmericanOdds } from './oddsUtils';

export const MARKET_CONDITIONS = {
  great_day: { label: 'Great Day', emoji: '🟢' },
  over_day: { label: 'Over Day', emoji: '🔴' },
  under_day: { label: 'Under Day', emoji: '🔵' },
  rough_day: { label: 'Rough Day', emoji: '⚫' },
  average_day: { label: 'Average Day', emoji: '🟡' },
} as const;

export type MarketCondition = keyof typeof MARKET_CONDITIONS;

/** Values stored before 2026-10-09, read as their replacements. */
const LEGACY_CONDITIONS: Record<string, MarketCondition> = {
  tough_day: 'rough_day',
  mixed_day: 'average_day',
};

function isMarketCondition(value: string): value is MarketCondition {
  return Object.hasOwn(MARKET_CONDITIONS, value);
}

/** Any stored or received value as a current condition; unknown values read as an Average Day. */
export function toMarketCondition(value: string | null | undefined): MarketCondition {
  if (!value) return 'average_day';
  if (isMarketCondition(value)) return value;
  return Object.hasOwn(LEGACY_CONDITIONS, value) ? LEGACY_CONDITIONS[value] : 'average_day';
}

export function marketConditionLabel(value: string | null | undefined): string {
  return MARKET_CONDITIONS[toMarketCondition(value)].label;
}

export function marketConditionEmoji(value: string | null | undefined): string {
  return MARKET_CONDITIONS[toMarketCondition(value)].emoji;
}

export type ConditionTone = 'positive' | 'negative' | 'neutral';

/** Label and tone for a value as received; unrecognised text keeps its wording, toned by the old card's keywords. */
export function conditionDisplay(value: string): { label: string; tone: ConditionTone } {
  if (isMarketCondition(value) || Object.hasOwn(LEGACY_CONDITIONS, value)) {
    const condition = toMarketCondition(value);
    const tone =
      condition === 'rough_day' ? 'negative' : condition === 'average_day' ? 'neutral' : 'positive';
    return { label: MARKET_CONDITIONS[condition].label, tone };
  }
  const tone = /hot|favorable/i.test(value)
    ? 'positive'
    : /cold|tough/i.test(value)
      ? 'negative'
      : 'neutral';
  return { label: value, tone };
}

export const PICK_DIRECTIONS = { over: 'Over', under: 'Under' } as const;
export type PickDirection = (typeof PICK_DIRECTIONS)[keyof typeof PICK_DIRECTIONS];

export type DirectionCall = 'up' | 'down' | 'even';

/** Why a direction got its call: `too_few_picks` means no record at this sample could be called. */
export type DirectionCallReason = 'called' | 'too_close' | 'too_few_picks' | 'no_picks';

/** One-sided tail of 68% confidence (one standard error), chosen by the owner on 2026-10-09. */
export const CALL_TAIL_PROBABILITY = (1 - 0.6827) / 2;

export interface DirectionPick {
  hit: boolean;
  /** Break-even probability implied by the pick's own odds */
  breakeven: number;
}

export interface DirectionResult {
  call: DirectionCall;
  reason: DirectionCallReason;
  picks: number;
  hits: number;
  /** Hit rate needed to break even, averaged over the picks (0-1); null with no picks */
  breakeven: number | null;
}

/** Break-even probability of an American price; null for an unusable one (impliedProbability would say 0.5). */
export function breakevenFromOdds(odds: string | number | null | undefined): number | null {
  const american =
    typeof odds === 'number' ? odds : Number(normalizeAmericanOdds(odds ?? null) ?? Number.NaN);
  if (!Number.isFinite(american) || Math.abs(american) < 100) return null;
  return american > 0 ? 100 / (american + 100) : -american / (-american + 100);
}

/** P(hits = k) for independent picks with different chances (a Poisson-binomial distribution). */
function hitDistribution(probabilities: number[]): number[] {
  let distribution = [1];
  for (const p of probabilities) {
    const next = new Array<number>(distribution.length + 1).fill(0);
    distribution.forEach((chance, k) => {
      next[k] += chance * (1 - p);
      next[k + 1] += chance * p;
    });
    distribution = next;
  }
  return distribution;
}

function sumFrom(values: number[], start: number, end = values.length): number {
  let total = 0;
  for (let i = start; i < end; i++) total += values[i];
  return total;
}

/** Up, down or even against break-even, by the exact chance of this record if every pick were priced right. */
export function callDirection(picks: DirectionPick[]): DirectionResult {
  if (picks.length === 0) {
    return { call: 'even', reason: 'no_picks', picks: 0, hits: 0, breakeven: null };
  }
  const hits = picks.filter((p) => p.hit).length;
  const probabilities = picks.map((p) => p.breakeven);
  const breakeven = probabilities.reduce((a, b) => a + b, 0) / picks.length;
  const distribution = hitDistribution(probabilities);
  const chanceOfAtLeast = sumFrom(distribution, hits);
  const chanceOfAtMost = sumFrom(distribution, 0, hits + 1);
  const result = { picks: picks.length, hits, breakeven };

  if (chanceOfAtLeast <= CALL_TAIL_PROBABILITY) return { ...result, call: 'up', reason: 'called' };
  if (chanceOfAtMost <= CALL_TAIL_PROBABILITY) return { ...result, call: 'down', reason: 'called' };
  const canEverBeCalled =
    distribution[distribution.length - 1] <= CALL_TAIL_PROBABILITY ||
    distribution[0] <= CALL_TAIL_PROBABILITY;
  return { ...result, call: 'even', reason: canEverBeCalled ? 'too_close' : 'too_few_picks' };
}

/** The day's label from its two directions; a day that lost money in one and broke even in the other is Rough. */
export function classifyDay(over: DirectionCall, under: DirectionCall): MarketCondition {
  if (over === 'up' && under === 'up') return 'great_day';
  if (over === 'up') return 'over_day';
  if (under === 'up') return 'under_day';
  if (over === 'even' && under === 'even') return 'average_day';
  return 'rough_day';
}
